import React, { useState, useEffect, useRef, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faCheck, faGripHorizontal } from "@fortawesome/pro-regular-svg-icons";
import { faLoader } from "@fortawesome/pro-solid-svg-icons";
import axios from "axios";
import { toast } from "react-toastify";
import NotificationToast from "../Helpers/NotificationToast";

export default function FileTypeMapModal({ file, onClose, onSuccess }) {
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [fileTypes, setFileTypes] = useState([]);
  const [selectedFileType, setSelectedFileType] = useState("");
  const [monthFrom, setMonthFrom] = useState("");
  const [monthTo, setMonthTo] = useState("");
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ isDragging: false, startX: 0, startY: 0 });

  const handleMouseDown = useCallback((e) => {
    dragRef.current = {
      isDragging: true,
      startX: e.clientX - position.x,
      startY: e.clientY - position.y,
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [position]);

  const handleMouseMove = useCallback((e) => {
    if (!dragRef.current.isDragging) return;
    setPosition({
      x: e.clientX - dragRef.current.startX,
      y: e.clientY - dragRef.current.startY,
    });
  }, []);

  const handleMouseUp = useCallback(() => {
    dragRef.current.isDragging = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  // Check if selected file type is Bank Statement
  const selectedFileTypeName = fileTypes.find(ft => ft.id == selectedFileType)?.name || "";
  const isBankStatement = selectedFileTypeName.toLowerCase().includes("bank statement");

  useEffect(() => {
    // Fetch file types from backend
    const fetchFileTypes = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}file-types`);
        const data = response.data;
        setFileTypes(Array.isArray(data) ? data : data?.file_types || []);
      } catch (error) {
        console.error("Failed to fetch file types:", error);
        toast(
          <NotificationToast
            title="Error"
            message="Failed to load file types"
            type="error"
          />
        );
      }
    };

    fetchFileTypes();
  }, []);

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => onClose(), 300);
  };

  const handleApprove = async () => {
    if (!selectedFileType && selectedFileType !== "no_map") {
      toast(
        <NotificationToast
          title="Selection Required"
          message="Please select a file type or choose 'No Map'"
          type="warning"
        />
      );
      return;
    }

    // Validate month selection for bank statements
    if (isBankStatement && !monthFrom) {
      toast(
        <NotificationToast
          title="Month Required"
          message="Please select at least the 'Month From' for bank statements"
          type="warning"
        />
      );
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}approve_file_with_type`, {
        file_id: file.id,
        file_type_id: selectedFileType === "no_map" ? null : selectedFileType,
        month_from: monthFrom || null,
        month_to: monthTo || null,
      });

      toast(
        <NotificationToast
          title="File Approved"
          message="File has been approved and mapped successfully"
          type="success"
        />
      );

      handleClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Failed to approve file:", error);
      toast(
        <NotificationToast
          title="Error"
          message={error.response?.data?.message || "Failed to approve file"}
          type="error"
        />
      );
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" style={{ pointerEvents: 'none' }}>
      <div
        className="absolute top-1/2 left-1/2 w-full max-w-lg rounded-lg bg-white text-left shadow-2xl ring-1 ring-gray-200"
        style={{
          transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
          pointerEvents: 'auto',
        }}
      >
        <div
          className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-t-lg border-b border-gray-200 cursor-move select-none"
          onMouseDown={handleMouseDown}
        >
          <div className="flex items-center gap-2 text-gray-400">
            <FontAwesomeIcon icon={faGripHorizontal} className="h-4 w-4" />
            <span className="text-xs">Drag to move</span>
          </div>
          <button
            type="button"
            className="rounded-md bg-gray-50 text-gray-400 hover:text-gray-500"
            onClick={handleClose}
          >
            <span className="sr-only">Close</span>
            <FontAwesomeIcon className="h-5 w-5" icon={faTimes} />
          </button>
        </div>

        <div className="px-4 pb-4 pt-5 sm:p-6">
          <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                    <FontAwesomeIcon
                      className="h-6 w-6 text-green-600"
                      icon={faCheck}
                    />
                  </div>
            <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
              <h3 className="text-lg font-semibold leading-6 text-gray-900">
                Approve File: {file.name}.{file.extension}
              </h3>
                    <div className="mt-4">
                      <p className="text-sm text-gray-500 mb-4">
                        Select the file type to map this file to, or choose "No Map" to approve without mapping.
                      </p>

                      <div className="space-y-4">
                        <div>
                          <label
                            htmlFor="file-type"
                            className="block text-sm font-medium text-gray-700"
                          >
                            File Type
                          </label>
                          <select
                            id="file-type"
                            value={selectedFileType}
                            onChange={(e) => {
                              setSelectedFileType(e.target.value);
                              setMonthFrom("");
                              setMonthTo("");
                            }}
                            className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-primary focus:outline-none focus:ring-primary sm:text-sm"
                            disabled={loading}
                          >
                            <option value="">-- Select File Type --</option>
                            <option value="no_map">No Map (Approve without type)</option>
                            {fileTypes.map((type) => (
                              <option key={type.id} value={type.id}>
                                {type.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {isBankStatement && (
                          <div className="space-y-3 pt-2 border-t border-gray-200">
                            <p className="text-xs text-gray-500">
                              Select the month range for this bank statement
                            </p>
                            <div>
                              <label
                                htmlFor="month-from"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Month From <span className="text-red-500">*</span>
                              </label>
                              <select
                                id="month-from"
                                value={monthFrom}
                                onChange={(e) => setMonthFrom(e.target.value)}
                                className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-primary focus:outline-none focus:ring-primary sm:text-sm"
                                disabled={loading}
                              >
                                <option value="">-- Select Month --</option>
                                {months.map((month) => (
                                  <option key={month} value={month}>
                                    {month}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label
                                htmlFor="month-to"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Month To <span className="text-gray-400 text-xs">(optional)</span>
                              </label>
                              <select
                                id="month-to"
                                value={monthTo}
                                onChange={(e) => setMonthTo(e.target.value)}
                                className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-primary focus:outline-none focus:ring-primary sm:text-sm"
                                disabled={loading}
                              >
                                <option value="">-- Select Month (Optional) --</option>
                                {months.map((month) => (
                                  <option key={month} value={month}>
                                    {month}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="inline-flex w-full justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleApprove}
              disabled={loading || !selectedFileType}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon
                    icon={faLoader}
                    className="animate-spin mr-2"
                  />
                  Approving...
                </>
              ) : (
                "Approve File"
              )}
            </button>
            <button
              type="button"
              className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
